import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell, LabelList } from "recharts";
import { TrendingUp } from "lucide-react";
import {
  buildQualityMeasureProjectionFootnote,
  QUALITY_MEASURE_PROJECTION_CARDS,
  type QualityMeasureProjectionCode,
} from "@act/shared/data-transfer-objects";
import SectionReveal from "./SectionReveal";
import SectionBanner from "./SectionBanner";
import { HostedReportSectionLayout } from '../HostedReportSectionLayout';
import { useReportsPrintStaticLayout } from '../hooks/useReportsPrintStaticLayout.hook';
import { foreground, muted, accent, border, body, CORAL, NAVY } from "./tokens";
import type { ReportHostedSectionProps } from '../reportHostedSection.types';

const PROJECTED_FILL = "#1F6192";

type MeasureCard = {
  code: QualityMeasureProjectionCode;
  headline: string;
  name: string;
  description: string;
  projected: number;
  target: number;
  tracking: string | null;
  source: 'warehouse' | 'mock';
};

const DEFAULT_MEASURES: MeasureCard[] = QUALITY_MEASURE_PROJECTION_CARDS.map(
  (card, index) => ({
    code: card.code,
    headline: card.headline,
    name: card.name,
    description: card.description,
    projected: [87.4, 12.5, 10.2, 33.3, 28.1, 12.5, 8.4, 100][index] ?? 0,
    target: [72.4, 16.4, 14.2, 47.7, 42.3, 34.3, 30.1, 30][index] ?? 0,
    tracking: null,
    source: 'mock' as const,
  }),
);

const isMeasureCard = (value: unknown): value is MeasureCard => {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as MeasureCard).code === 'string' &&
    typeof (value as MeasureCard).projected === 'number' &&
    typeof (value as MeasureCard).target === 'number'
  );
};

/**
 * Resolves measure cards from snapshot JSON, falling back to static defaults.
 * @param data - quality-measure-projections snapshot payload.
 * @returns Measure cards for chart rendering (only metrics present in the snapshot).
 */
const resolveMeasureCards = (data: Record<string, unknown>): MeasureCard[] => {
  if (!Array.isArray(data.measures)) {
    return DEFAULT_MEASURES;
  }

  const byCode = new Map<QualityMeasureProjectionCode, MeasureCard>();
  for (const entry of data.measures) {
    if (!isMeasureCard(entry)) {
      continue;
    }
    const config = QUALITY_MEASURE_PROJECTION_CARDS.find(
      card => card.code === entry.code,
    );
    byCode.set(entry.code, {
      code: entry.code,
      headline:
        typeof (entry as { headline?: unknown }).headline === 'string' &&
        (entry as { headline: string }).headline.trim()
          ? (entry as { headline: string }).headline
          : (config?.headline ?? entry.code),
      name: config?.name ?? entry.name,
      description:
        typeof entry.description === 'string' && entry.description.trim()
          ? entry.description
          : (config?.description ?? ''),
      projected: entry.projected,
      target: entry.target,
      tracking:
        typeof (entry as { tracking?: unknown }).tracking === 'string'
          ? (entry as { tracking: string }).tracking
          : null,
      source: entry.source === 'warehouse' ? 'warehouse' : 'mock',
    });
  }

  if (byCode.size === 0) {
    return DEFAULT_MEASURES;
  }

  return QUALITY_MEASURE_PROJECTION_CARDS.filter(card => byCode.has(card.code)).map(
    card => byCode.get(card.code)!,
  );
};

/**
 * Quality measure projection cards backed by measurement-period warehouse metrics.
 * @param props - Resolved quality-measure-projections snapshot.
 */
const MeasureProjections = ({ data }: ReportHostedSectionProps) => {
  const measures = resolveMeasureCards(data);
  const printStatic = useReportsPrintStaticLayout();
  const chartHeight = printStatic ? 82 : 140;
  const chartMargin = printStatic
    ? { left: 0, right: 44, top: 12, bottom: 0 }
    : { left: 0, right: 56, top: 18, bottom: 4 };

  return (
  <HostedReportSectionLayout
    sectionDomId="measures"
    className="section--white report-hosted-section-body"
    style={{ background: "var(--white)", fontFamily: body }}
    banner={
      <SectionBanner
        title="Quality Measure Projections"
        iconHref="#icon_relationship_engine"
      />
    }
  >

      <SectionReveal delay={0.1}>
        <div className="quality-measure-projections-grid grid md:grid-cols-2 gap-6">
          {measures.map((m) => {
            const chartData = [{ name: m.headline, projected: m.projected }];
            const footnote = buildQualityMeasureProjectionFootnote(
              m.projected,
              m.target,
              m.tracking,
            );
            const yMax = Math.max(m.target, m.projected) * 1.25;
            return (
              <div
                key={m.code}
                className="quality-measure-projection-card flex h-full flex-col rounded-2xl p-6 shadow-sm"
                style={{ background: "#fff", border: `1px solid ${border}` }}
              >
                <div className="flex items-start justify-between mb-1 gap-3">
                  <div>
                    <div className="flex items-baseline gap-2">
                      <h3 className="text-lg font-semibold" style={{ color: foreground, fontFamily: "'Lora', serif" }}>
                        {m.headline}
                      </h3>
                    </div>
                    <p className="text-sm font-medium" style={{ color: foreground }}>
                      {m.name}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <div
                      className="text-2xl font-bold tabular-nums"
                      style={{ color: foreground, fontFamily: "'Lora', serif" }}
                    >
                      {Math.round(m.projected)}%
                    </div>
                    <div className="text-[10px] uppercase tracking-wider" style={{ color: muted }}>
                      Projected
                    </div>
                  </div>
                </div>
                <p className="quality-measure-projection-description text-xs mb-4" style={{ color: muted }}>
                  {m.description}
                </p>

                <div className="quality-measure-projection-chart">
                <ResponsiveContainer width="100%" height={chartHeight}>
                  <BarChart data={chartData} layout="vertical" margin={chartMargin}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8E0D6" horizontal={false} />
                    <XAxis
                      type="number"
                      domain={[0, yMax]}
                      tickFormatter={(v) => `${v}%`}
                      tick={{ fontSize: 11 }}
                      stroke="#6B7A8D"
                    />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={72} stroke="#6B7A8D" />
                    <Tooltip
                      contentStyle={{ borderRadius: 12, border: "1px solid #E8E0D6", fontSize: 13 }}
                      formatter={(value: number) => [`${Math.round(value)}%`, "Projected"]}
                    />
                    <ReferenceLine
                      x={m.target}
                      stroke={CORAL}
                      strokeDasharray="4 4"
                      strokeWidth={1.5}
                      label={{ value: `Target ${m.target.toFixed(1)}%`, position: "top", fill: CORAL, fontSize: 11, fontWeight: 600 }}
                    />
                    <Bar dataKey="projected" radius={[0, 6, 6, 0]} barSize={28}>
                      <Cell fill={PROJECTED_FILL} />
                      <LabelList
                        dataKey="projected"
                        position="right"
                        formatter={(v: number) => `${Math.round(v)}%`}
                        style={{ fill: NAVY, fontSize: 12, fontWeight: 600 }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                </div>

                <p className="quality-measure-projection-footnote text-xs mt-3" style={{ color: muted }}>
                  {footnote.isAboveTarget ? (
                    <span className="inline-flex items-center gap-1.5 font-semibold" style={{ color: accent }}>
                      <TrendingUp className="w-3.5 h-3.5" />
                      {footnote.deltaPts} pts above target
                    </span>
                  ) : (
                    <span>
                      <strong style={{ color: foreground }}>
                        {Math.abs(footnote.deltaPts)} pts
                      </strong>{' '}
                      to target {"\u2014"} {footnote.statusPhrase}.
                    </span>
                  )}
                </p>
              </div>
            );
          })}
        </div>
      </SectionReveal>
  </HostedReportSectionLayout>
  );
};

export default MeasureProjections;
